import { ArrowLeft, ExternalLink, Play } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import Gallery from "../components/Gallery";
import JsonLd from "../components/JsonLd";
import { Section } from "../components/Section";
import { findProject } from "../data/projects";
import { site } from "../data/site";

export default function ProjectPage() {
  const { slug } = useParams();
  const project = findProject(slug);

  if (!project) {
    return (
      <section className="page-hero compact">
        <p className="eyebrow">Project not found</p>
        <h1>This project page is not available.</h1>
        <Link className="text-link" to="/games"><ArrowLeft size={16} aria-hidden="true" /> Back to games</Link>
      </section>
    );
  }

  return (
    <>
      <JsonLd
        id={`schema-${project.slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "VideoGame",
          name: project.title,
          description: project.description,
          url: `${site.baseUrl}/games/${project.slug}`,
          gamePlatform: project.platforms,
          publisher: project.slug === "jeff" ? "SZ INTERACTIVE LTD" : "SZ Interactive"
        }}
      />
      <section className="project-hero" style={{ "--accent": project.accent } as React.CSSProperties}>
        <img src={project.heroImage} alt={`${project.title} key art`} />
        <div className="project-hero-copy">
          <Link className="text-link back-link" to="/games"><ArrowLeft size={16} aria-hidden="true" /> Work</Link>
          <p className="eyebrow">{project.mood ?? project.status}</p>
          <h1>{project.title}</h1>
          <p>{project.subtitle}</p>
          <div className="badge-row">
            {project.platforms.map((platform) => <span className="badge" key={platform}>{platform}</span>)}
            {project.status && <span className="badge hot">{project.status}</span>}
          </div>
          <div className="action-row">
            {project.storeUrl && <a className="button primary" href={project.storeUrl} target="_blank" rel="noreferrer">{project.ctaLabel} <ExternalLink size={18} aria-hidden="true" /></a>}
          </div>
          {project.credits && <p className="credits">{project.credits}</p>}
        </div>
      </section>

      <Section eyebrow="Overview" title={project.description}>
        <div className="project-detail-grid">
          <div className="detail-panel">
            <h2>Roles</h2>
            <div className="badge-row">{project.roles.map((role) => <span className="badge" key={role}>{role}</span>)}</div>
          </div>
          <div className="detail-panel">
            <h2>Technology</h2>
            <div className="badge-row">{project.technologies?.map((tech) => <span className="badge" key={tech}>{tech}</span>)}</div>
          </div>
        </div>
      </Section>

      {project.featureGroups && (
        <Section eyebrow="Design notes" title={project.slug === "jeff" ? "Readable chaos, fast restarts, co-op pressure." : "A quiet place that keeps changing."}>
          <div className="feature-grid">
            {project.featureGroups.map((feature) => (
              <article key={feature.title} className="feature-card">
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </Section>
      )}

      {project.trailers && (
        <Section eyebrow="Trailer" title={project.trailers.length > 1 ? "Official trailers" : "Official trailer"}>
          <div className={project.trailers.length > 1 ? "trailer-grid" : undefined}>
            {project.trailers.map((trailer) => (
              <a className="trailer-poster" href={trailer.url} target="_blank" rel="noreferrer" key={trailer.url}>
                <img src={trailer.poster} alt={`${trailer.title} poster`} loading="lazy" />
                <span><Play size={18} aria-hidden="true" /> {trailer.source === "Steam" ? "Open trailer on Steam" : "Watch trailer"}</span>
              </a>
            ))}
          </div>
        </Section>
      )}

      {project.screenshots && (
        <Section eyebrow="Screenshots" title="Official media">
          <Gallery images={project.screenshots} title={project.title} />
        </Section>
      )}

    </>
  );
}
